import filters from '@/services/Filters.js'
import { TRANSLATIONS_TOGGLE } from '@/services/helpers/TranslationsHelper.js'
import UtilityHelper from '@/services/helpers/UtilityHelper.js'
import SiteConfig from '@/services/SiteConfig.js'
import { createI18n } from 'vue-i18n'

// The locale follows the site, which on the server resolves per request from
// the Host header (VIP-12037) — so cache one service per locale, not one
// per process.
const services = new Map()

let _requestServiceResolver = null

/**
 * Installed by the server entry: returns the rendering request's i18n service
 * (or undefined outside a request), so concurrent renders of different sites
 * cannot translate through each other's locale (VIP-12037).
 */
export const setRequestI18nResolver = (resolver) => {
  _requestServiceResolver = resolver
}

// Outside a request the site (and therefore the locale) is the process-wide
// one, so the per-locale cache is the fallback — a mutable "last created"
// variable could hand a concurrent request's locale to this one.
const _activeService = () =>
  _requestServiceResolver?.() ?? services.get(SiteConfig.lang)
// The toggle is a client-side dev tool; no localStorage on the server
let enabled = import.meta.env.SSR || !localStorage.getItem(TRANSLATIONS_TOGGLE)

const setupTranslations = async () => {
  if (services.has(SiteConfig.lang)) return services.get(SiteConfig.lang)

  const locale = SiteConfig.lang
  const fallbackLocale = 'de-DE'
  const [{ default: translations }, { default: fallback }] = await Promise.all([
    await import(`@/assets/translations/${locale}.json`),
    await import(`@/assets/translations/${fallbackLocale}.json`)
  ])
  const messages = {
    [locale]: translations,
    [fallbackLocale]: fallback
  }

  const service = createI18n({
    legacy: false,
    warnHtmlMessage: false,
    locale,
    fallbackLocale,
    messages,
    missingWarn: false
  })
  services.set(locale, service)

  return service
}

const translate = (key, ...data) => {
  if (!enabled) return key
  const activeService = _activeService()
  if (!activeService?.global) return key
  return UtilityHelper.formatString(activeService.global.t(key, data), ...data)
}

const formatCurrency = (number, fraction = 2) => {
  if (
    Number.isNaN(Number(number)) ||
    typeof number === 'boolean' ||
    typeof number === 'object'
  ) {
    console.error('Invalid number was passed to formatCurrency')
    return ''
  }
  return translate(
    'fe_app_pdp_final_price_format',
    filters.float(number, fraction, false)
  )
}

// init and install have to be done separately because it cannot work with async
const install = (app, i18n) => {
  app.use(i18n)

  app.config.globalProperties.translate = (key, ...params) =>
    translate(key, ...params)
  app.config.globalProperties.formatCurrency = (number, fraction) =>
    formatCurrency(number, fraction)
}

export { formatCurrency, install, setupTranslations, translate }
